import { useState } from 'react';
import { Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useEvents } from '../hooks/useEvents';
import { EventsTable } from '../components/calendar/EventsTable';
import { AdminCodeDialog } from '../components/calendar/AdminCodeDialog';
import { AddEventDialog } from '../components/calendar/AddEventDialog';
import { DeleteEventDialog } from '../components/calendar/DeleteEventDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BIBLE_STUDY_ZOOM = "https://us05web.zoom.us/j/81120587177";
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const LAST_MONTH_INDEX = 11;
const FIRST_MONTH_INDEX = 0;

const EMPTY_FORM = {
  title: '', date: '', time: '', description: '',
  recurring: false, recurrence_type: '', day_of_week: '', nth_week: '', zoom_link: '',
};

const getNthDayOfMonth = (year, month, dayOfWeek, n) => {
  const dates = [];
  const date = new Date(year, month, 1);
  while (date.getMonth() === month) {
    if (date.getDay() === dayOfWeek) dates.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return n <= dates.length ? dates[n - 1] : null;
};

const addEventToDay = (dayEvents, dayKey, date, event) => {
  if (!dayEvents[dayKey]) dayEvents[dayKey] = { date: new Date(date), events: [] };
  dayEvents[dayKey].events.push(event);
};

const generateEventsForMonth = (events, year, month) => {
  const dayEvents = {};

  for (const event of events) {
    if (!event.recurring && !event.date) continue;

    if (event.recurring && event.recurrence_type === 'weekly') {
      const date = new Date(year, month, 1);
      while (date.getMonth() === month) {
        if (date.getDay() === event.day_of_week) {
          addEventToDay(dayEvents, date.getDate(), date, event);
        }
        date.setDate(date.getDate() + 1);
      }
      continue;
    }

    if (event.recurring && event.recurrence_type === 'monthly_nth') {
      const targetDate = getNthDayOfMonth(year, month, event.day_of_week, event.nth_week);
      if (targetDate) addEventToDay(dayEvents, targetDate.getDate(), targetDate, event);
      continue;
    }

    if (event.date) {
      const eventDate = new Date(event.date + 'T00:00:00');
      if (eventDate.getFullYear() === year && eventDate.getMonth() === month) {
        addEventToDay(dayEvents, eventDate.getDate(), eventDate, event);
      }
    }
  }

  return Object.values(dayEvents).sort((a, b) => a.date - b.date);
};

const buildEventBody = (eventForm, adminCode) => {
  const body = {
    title: eventForm.title,
    time: eventForm.time,
    description: eventForm.description,
    recurring: eventForm.recurring,
    admin_code: adminCode,
  };

  if (eventForm.recurring) {
    body.recurrence_type = eventForm.recurrence_type;
    body.day_of_week = parseInt(eventForm.day_of_week);
    if (eventForm.recurrence_type === 'monthly_nth') {
      body.nth_week = parseInt(eventForm.nth_week);
    }
  } else {
    body.date = eventForm.date;
  }

  if (eventForm.title.toLowerCase().includes('bible study')) {
    body.zoom_link = BIBLE_STUDY_ZOOM;
  } else if (eventForm.zoom_link) {
    body.zoom_link = eventForm.zoom_link;
  }

  return body;
};

export const CalendarPage = () => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const { events, loading, refetch } = useEvents();

  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [adminCode, setAdminCode] = useState('');
  const [adminVerified, setAdminVerified] = useState(false);
  const [adminError, setAdminError] = useState('');

  const [showEventForm, setShowEventForm] = useState(false);
  const [eventForm, setEventForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const calendarData = generateEventsForMonth(events, currentYear, currentMonth);

  const prevMonth = () => {
    if (currentMonth === FIRST_MONTH_INDEX) { setCurrentMonth(LAST_MONTH_INDEX); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (currentMonth === LAST_MONTH_INDEX) { setCurrentMonth(FIRST_MONTH_INDEX); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  const handleAdminSubmit = async () => {
    setAdminError('');
    try {
      const res = await fetch(`${API_URL}/api/events/verify-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_code: adminCode }),
      });
      if (res.ok) {
        setAdminVerified(true);
        setShowAdminDialog(false);
        setShowEventForm(true);
      } else {
        setAdminError('Invalid admin code. Please try again.');
      }
    } catch {
      setAdminError('Connection error. Please try again.');
    }
  };

  const handleAddEventClick = () => {
    if (adminVerified) {
      setShowEventForm(true);
    } else {
      setAdminCode('');
      setAdminError('');
      setShowAdminDialog(true);
    }
  };

  const handleEventSubmit = async () => {
    if (!eventForm.title || !eventForm.time) {
      toast.error('Title and time are required');
      return;
    }
    if (!eventForm.recurring && !eventForm.date) {
      toast.error('Date is required for non-recurring events');
      return;
    }
    if (eventForm.recurring && !eventForm.recurrence_type) {
      toast.error('Please select a recurrence type');
      return;
    }

    setSubmitting(true);
    try {
      const body = buildEventBody(eventForm, adminCode);

      const res = await fetch(`${API_URL}/api/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        toast.success('Event added successfully!');
        setShowEventForm(false);
        setEventForm(EMPTY_FORM);
        refetch();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add event');
      }
    } catch {
      toast.error('Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}?admin_code=${adminCode}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Event deleted');
        setDeleteTarget(null);
        refetch();
      } else {
        toast.error('Failed to delete event');
      }
    } catch {
      toast.error('Connection error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-800" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CalendarIcon className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
          <h1 className="text-7xl md:text-8xl font-bold text-white mb-4">Events Calendar</h1>
          <p className="text-2xl text-yellow-300">Join us for worship and fellowship</p>
        </div>
      </section>

      {/* Calendar Table */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <Button data-testid="prev-month-btn" variant="outline" onClick={prevMonth} className="border-2 border-blue-300 text-blue-800 hover:bg-blue-50">
              <ChevronLeft className="h-5 w-5 mr-1" /> Previous
            </Button>
            <h2 className="text-5xl md:text-6xl font-bold text-blue-800">
              {MONTHS[currentMonth]} {currentYear}
            </h2>
            <Button data-testid="next-month-btn" variant="outline" onClick={nextMonth} className="border-2 border-blue-300 text-blue-800 hover:bg-blue-50">
              Next <ChevronRight className="h-5 w-5 ml-1" />
            </Button>
          </div>

          <div className="flex justify-end mb-6">
            <Button data-testid="add-event-btn" onClick={handleAddEventClick} className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold px-6 py-3 text-lg">
              <Plus className="h-5 w-5 mr-2" /> Add Event
            </Button>
          </div>

          <EventsTable
            calendarData={calendarData}
            loading={loading}
            currentMonth={currentMonth}
            currentYear={currentYear}
            adminVerified={adminVerified}
            onDelete={setDeleteTarget}
          />
        </div>
      </section>

      {/* Plan Your Visit */}
      <section className="py-16 bg-gradient-to-br from-yellow-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-blue-800 mb-4">Plan Your Visit</h2>
          <p className="text-xl text-blue-700 mb-8" style={{ fontFamily: "'Crimson Text', Georgia, serif" }}>
            We look forward to welcoming you to our church family. Come as you are!
          </p>
        </div>
      </section>

      <AdminCodeDialog
        open={showAdminDialog}
        onOpenChange={setShowAdminDialog}
        adminCode={adminCode}
        setAdminCode={setAdminCode}
        onSubmit={handleAdminSubmit}
        error={adminError}
      />

      <AddEventDialog
        open={showEventForm}
        onOpenChange={(open) => { setShowEventForm(open); if (!open) setEventForm(EMPTY_FORM); }}
        form={eventForm}
        setForm={setEventForm}
        onSubmit={handleEventSubmit}
        submitting={submitting}
      />

      <DeleteEventDialog
        event={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};
